/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteConnectorPathParameters,
  DisableConnectorPathParameters,
  EnableConnectorPathParameters,
  GetAllConnectorsParams,
  GetConnectorByIdPathParameters,
  PreviewConnectorNameParams,
  TLConnectorCreatedResponse,
  TLConnectorCreateRequest,
  TLConnectorListResponse,
  TLConnectorNamePreviewResponse,
  TLConnectorResponse,
  TLConnectorUpdateRequest,
  TLHTTPValidationError,
  TLSuccessResponse,
  UpdateConnectorPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get All Connectors
 */
export const getAllConnectors = (
  params?: GetAllConnectorsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectorListResponse>({ url: `/data-connectors`, method: 'GET', params, signal }, options);
};

export const getGetAllConnectorsQueryKey = (params?: GetAllConnectorsParams) => {
  return ['data-connectors', ...(params ? [params] : [])] as const;
};

export const getGetAllConnectorsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllConnectors>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllConnectorsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllConnectors>>> = ({ signal }) =>
    getAllConnectors(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllConnectors>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllConnectorsQueryResult = NonNullable<Awaited<ReturnType<typeof getAllConnectors>>>;
export type GetAllConnectorsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAllConnectors<
  TData = Awaited<ReturnType<typeof getAllConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetAllConnectorsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllConnectors>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllConnectors>>,
          TError,
          Awaited<ReturnType<typeof getAllConnectors>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllConnectors<
  TData = Awaited<ReturnType<typeof getAllConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllConnectors>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllConnectors>>,
          TError,
          Awaited<ReturnType<typeof getAllConnectors>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllConnectors<
  TData = Awaited<ReturnType<typeof getAllConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllConnectors>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Connectors
 */

export function useGetAllConnectors<
  TData = Awaited<ReturnType<typeof getAllConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllConnectors>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllConnectorsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Connectors
 */
export const prefetchGetAllConnectorsQuery = async <
  TData = Awaited<ReturnType<typeof getAllConnectors>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetAllConnectorsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllConnectors>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllConnectorsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Connectors
 */
export const invalidateGetAllConnectors = async (
  queryClient: QueryClient,
  params?: GetAllConnectorsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllConnectorsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Connector
 */
export const createConnector = (
  tLConnectorCreateRequest: BodyType<TLConnectorCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectorCreatedResponse>(
    {
      url: `/data-connectors`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLConnectorCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateConnectorQueryKey = (tLConnectorCreateRequest?: BodyType<TLConnectorCreateRequest>) => {
  return ['POST', 'data-connectors', tLConnectorCreateRequest] as const;
};

export const getCreateConnectorQueryOptions = <
  TData = Awaited<ReturnType<typeof createConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectorCreateRequest: BodyType<TLConnectorCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateConnectorQueryKey(tLConnectorCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createConnector>>> = ({ signal }) =>
    createConnector(tLConnectorCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createConnector>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateConnectorQueryResult = NonNullable<Awaited<ReturnType<typeof createConnector>>>;
export type CreateConnectorQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateConnector<
  TData = Awaited<ReturnType<typeof createConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectorCreateRequest: BodyType<TLConnectorCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnector>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConnector>>,
          TError,
          Awaited<ReturnType<typeof createConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConnector<
  TData = Awaited<ReturnType<typeof createConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectorCreateRequest: BodyType<TLConnectorCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnector>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConnector>>,
          TError,
          Awaited<ReturnType<typeof createConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConnector<
  TData = Awaited<ReturnType<typeof createConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectorCreateRequest: BodyType<TLConnectorCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Connector
 */

export function useCreateConnector<
  TData = Awaited<ReturnType<typeof createConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConnectorCreateRequest: BodyType<TLConnectorCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateConnectorQueryOptions(tLConnectorCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Connector
 */
export const prefetchCreateConnectorQuery = async <
  TData = Awaited<ReturnType<typeof createConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLConnectorCreateRequest: BodyType<TLConnectorCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateConnectorQueryOptions(tLConnectorCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Connector
 */
export const invalidateCreateConnector = async (
  queryClient: QueryClient,
  tLConnectorCreateRequest: BodyType<TLConnectorCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateConnectorQueryKey(tLConnectorCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary Preview Connector Name
 */
export const previewConnectorName = (
  params: PreviewConnectorNameParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectorNamePreviewResponse>(
    { url: `/data-connectors/name-preview`, method: 'GET', params, signal },
    options,
  );
};

export const getPreviewConnectorNameQueryKey = (params?: PreviewConnectorNameParams) => {
  return ['data-connectors', 'name-preview', ...(params ? [params] : [])] as const;
};

export const getPreviewConnectorNameQueryOptions = <
  TData = Awaited<ReturnType<typeof previewConnectorName>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: PreviewConnectorNameParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewConnectorName>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPreviewConnectorNameQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof previewConnectorName>>> = ({ signal }) =>
    previewConnectorName(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof previewConnectorName>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PreviewConnectorNameQueryResult = NonNullable<Awaited<ReturnType<typeof previewConnectorName>>>;
export type PreviewConnectorNameQueryError = ErrorType<TLHTTPValidationError>;

export function usePreviewConnectorName<
  TData = Awaited<ReturnType<typeof previewConnectorName>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: PreviewConnectorNameParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewConnectorName>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewConnectorName>>,
          TError,
          Awaited<ReturnType<typeof previewConnectorName>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewConnectorName<
  TData = Awaited<ReturnType<typeof previewConnectorName>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: PreviewConnectorNameParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewConnectorName>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof previewConnectorName>>,
          TError,
          Awaited<ReturnType<typeof previewConnectorName>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePreviewConnectorName<
  TData = Awaited<ReturnType<typeof previewConnectorName>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: PreviewConnectorNameParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewConnectorName>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview Connector Name
 */

export function usePreviewConnectorName<
  TData = Awaited<ReturnType<typeof previewConnectorName>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: PreviewConnectorNameParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewConnectorName>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPreviewConnectorNameQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Preview Connector Name
 */
export const prefetchPreviewConnectorNameQuery = async <
  TData = Awaited<ReturnType<typeof previewConnectorName>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: PreviewConnectorNameParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof previewConnectorName>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPreviewConnectorNameQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Preview Connector Name
 */
export const invalidatePreviewConnectorName = async (
  queryClient: QueryClient,
  params: PreviewConnectorNameParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getPreviewConnectorNameQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Delete Connector
 */
export const deleteConnector = (
  { connectorId }: DeleteConnectorPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>({ url: `/data-connectors/${connectorId}`, method: 'DELETE', signal }, options);
};

export const getDeleteConnectorQueryKey = ({ connectorId }: DeleteConnectorPathParameters) => {
  return ['DELETE', 'data-connectors', connectorId] as const;
};

export const getDeleteConnectorQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: DeleteConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteConnectorQueryKey({ connectorId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteConnector>>> = ({ signal }) =>
    deleteConnector({ connectorId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectorId !== null && connectorId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteConnector>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteConnectorQueryResult = NonNullable<Awaited<ReturnType<typeof deleteConnector>>>;
export type DeleteConnectorQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteConnector<
  TData = Awaited<ReturnType<typeof deleteConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConnectorPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnector>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConnector>>,
          TError,
          Awaited<ReturnType<typeof deleteConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConnector<
  TData = Awaited<ReturnType<typeof deleteConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnector>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConnector>>,
          TError,
          Awaited<ReturnType<typeof deleteConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConnector<
  TData = Awaited<ReturnType<typeof deleteConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Connector
 */

export function useDeleteConnector<
  TData = Awaited<ReturnType<typeof deleteConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: DeleteConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteConnectorQueryOptions({ connectorId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Connector
 */
export const prefetchDeleteConnectorQuery = async <
  TData = Awaited<ReturnType<typeof deleteConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectorId }: DeleteConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteConnectorQueryOptions({ connectorId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Connector
 */
export const invalidateDeleteConnector = async (
  queryClient: QueryClient,
  { connectorId }: DeleteConnectorPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteConnectorQueryKey({ connectorId }) }, options);

  return queryClient;
};

/**
 * @summary Get Connector By Id
 */
export const getConnectorById = (
  { connectorId }: GetConnectorByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectorResponse>({ url: `/data-connectors/${connectorId}`, method: 'GET', signal }, options);
};

export const getGetConnectorByIdQueryKey = ({ connectorId }: GetConnectorByIdPathParameters) => {
  return ['data-connectors', connectorId] as const;
};

export const getGetConnectorByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getConnectorById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: GetConnectorByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectorById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConnectorByIdQueryKey({ connectorId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConnectorById>>> = ({ signal }) =>
    getConnectorById({ connectorId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectorId !== null && connectorId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getConnectorById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetConnectorByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getConnectorById>>>;
export type GetConnectorByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetConnectorById<
  TData = Awaited<ReturnType<typeof getConnectorById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConnectorByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectorById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectorById>>,
          TError,
          Awaited<ReturnType<typeof getConnectorById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectorById<
  TData = Awaited<ReturnType<typeof getConnectorById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConnectorByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectorById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConnectorById>>,
          TError,
          Awaited<ReturnType<typeof getConnectorById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConnectorById<
  TData = Awaited<ReturnType<typeof getConnectorById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConnectorByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectorById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Connector By Id
 */

export function useGetConnectorById<
  TData = Awaited<ReturnType<typeof getConnectorById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: GetConnectorByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectorById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConnectorByIdQueryOptions({ connectorId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Connector By Id
 */
export const prefetchGetConnectorByIdQuery = async <
  TData = Awaited<ReturnType<typeof getConnectorById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectorId }: GetConnectorByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConnectorById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConnectorByIdQueryOptions({ connectorId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Connector By Id
 */
export const invalidateGetConnectorById = async (
  queryClient: QueryClient,
  { connectorId }: GetConnectorByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConnectorByIdQueryKey({ connectorId }) }, options);

  return queryClient;
};

/**
 * @summary Update Connector
 */
export const updateConnector = (
  { connectorId }: UpdateConnectorPathParameters,
  tLConnectorUpdateRequest: BodyType<TLConnectorUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/data-connectors/${connectorId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLConnectorUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateConnectorQueryKey = (
  { connectorId }: UpdateConnectorPathParameters,
  tLConnectorUpdateRequest?: BodyType<TLConnectorUpdateRequest>,
) => {
  return ['PATCH', 'data-connectors', connectorId, tLConnectorUpdateRequest] as const;
};

export const getUpdateConnectorQueryOptions = <
  TData = Awaited<ReturnType<typeof updateConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: UpdateConnectorPathParameters,
  tLConnectorUpdateRequest: BodyType<TLConnectorUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateConnectorQueryKey({ connectorId }, tLConnectorUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateConnector>>> = ({ signal }) =>
    updateConnector({ connectorId }, tLConnectorUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectorId !== null && connectorId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateConnector>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateConnectorQueryResult = NonNullable<Awaited<ReturnType<typeof updateConnector>>>;
export type UpdateConnectorQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateConnector<
  TData = Awaited<ReturnType<typeof updateConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConnectorPathParameters,
  tLConnectorUpdateRequest: BodyType<TLConnectorUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnector>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateConnector>>,
          TError,
          Awaited<ReturnType<typeof updateConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateConnector<
  TData = Awaited<ReturnType<typeof updateConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConnectorPathParameters,
  tLConnectorUpdateRequest: BodyType<TLConnectorUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnector>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateConnector>>,
          TError,
          Awaited<ReturnType<typeof updateConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateConnector<
  TData = Awaited<ReturnType<typeof updateConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConnectorPathParameters,
  tLConnectorUpdateRequest: BodyType<TLConnectorUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Connector
 */

export function useUpdateConnector<
  TData = Awaited<ReturnType<typeof updateConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: UpdateConnectorPathParameters,
  tLConnectorUpdateRequest: BodyType<TLConnectorUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateConnectorQueryOptions({ connectorId }, tLConnectorUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Connector
 */
export const prefetchUpdateConnectorQuery = async <
  TData = Awaited<ReturnType<typeof updateConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectorId }: UpdateConnectorPathParameters,
  tLConnectorUpdateRequest: BodyType<TLConnectorUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateConnectorQueryOptions({ connectorId }, tLConnectorUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Connector
 */
export const invalidateUpdateConnector = async (
  queryClient: QueryClient,
  { connectorId }: UpdateConnectorPathParameters,
  tLConnectorUpdateRequest: BodyType<TLConnectorUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateConnectorQueryKey({ connectorId }, tLConnectorUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Disable Connector
 */
export const disableConnector = (
  { connectorId }: DisableConnectorPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/data-connectors/${connectorId}/disable`, method: 'PATCH', signal },
    options,
  );
};

export const getDisableConnectorQueryKey = ({ connectorId }: DisableConnectorPathParameters) => {
  return ['PATCH', 'data-connectors', connectorId, 'disable'] as const;
};

export const getDisableConnectorQueryOptions = <
  TData = Awaited<ReturnType<typeof disableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: DisableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDisableConnectorQueryKey({ connectorId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof disableConnector>>> = ({ signal }) =>
    disableConnector({ connectorId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectorId !== null && connectorId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof disableConnector>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DisableConnectorQueryResult = NonNullable<Awaited<ReturnType<typeof disableConnector>>>;
export type DisableConnectorQueryError = ErrorType<TLHTTPValidationError>;

export function useDisableConnector<
  TData = Awaited<ReturnType<typeof disableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DisableConnectorPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnector>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof disableConnector>>,
          TError,
          Awaited<ReturnType<typeof disableConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDisableConnector<
  TData = Awaited<ReturnType<typeof disableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DisableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnector>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof disableConnector>>,
          TError,
          Awaited<ReturnType<typeof disableConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDisableConnector<
  TData = Awaited<ReturnType<typeof disableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DisableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Disable Connector
 */

export function useDisableConnector<
  TData = Awaited<ReturnType<typeof disableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: DisableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDisableConnectorQueryOptions({ connectorId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Disable Connector
 */
export const prefetchDisableConnectorQuery = async <
  TData = Awaited<ReturnType<typeof disableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectorId }: DisableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof disableConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDisableConnectorQueryOptions({ connectorId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Disable Connector
 */
export const invalidateDisableConnector = async (
  queryClient: QueryClient,
  { connectorId }: DisableConnectorPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDisableConnectorQueryKey({ connectorId }) }, options);

  return queryClient;
};

/**
 * @summary Enable Connector
 */
export const enableConnector = (
  { connectorId }: EnableConnectorPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/data-connectors/${connectorId}/enable`, method: 'PATCH', signal },
    options,
  );
};

export const getEnableConnectorQueryKey = ({ connectorId }: EnableConnectorPathParameters) => {
  return ['PATCH', 'data-connectors', connectorId, 'enable'] as const;
};

export const getEnableConnectorQueryOptions = <
  TData = Awaited<ReturnType<typeof enableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: EnableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getEnableConnectorQueryKey({ connectorId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof enableConnector>>> = ({ signal }) =>
    enableConnector({ connectorId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectorId !== null && connectorId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof enableConnector>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type EnableConnectorQueryResult = NonNullable<Awaited<ReturnType<typeof enableConnector>>>;
export type EnableConnectorQueryError = ErrorType<TLHTTPValidationError>;

export function useEnableConnector<
  TData = Awaited<ReturnType<typeof enableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: EnableConnectorPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnector>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof enableConnector>>,
          TError,
          Awaited<ReturnType<typeof enableConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEnableConnector<
  TData = Awaited<ReturnType<typeof enableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: EnableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnector>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof enableConnector>>,
          TError,
          Awaited<ReturnType<typeof enableConnector>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEnableConnector<
  TData = Awaited<ReturnType<typeof enableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: EnableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Enable Connector
 */

export function useEnableConnector<
  TData = Awaited<ReturnType<typeof enableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { connectorId }: EnableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getEnableConnectorQueryOptions({ connectorId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Enable Connector
 */
export const prefetchEnableConnectorQuery = async <
  TData = Awaited<ReturnType<typeof enableConnector>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { connectorId }: EnableConnectorPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof enableConnector>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getEnableConnectorQueryOptions({ connectorId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Enable Connector
 */
export const invalidateEnableConnector = async (
  queryClient: QueryClient,
  { connectorId }: EnableConnectorPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getEnableConnectorQueryKey({ connectorId }) }, options);

  return queryClient;
};
